// Pure reconciliation of a `call.sync` snapshot against the local call machine
// (docs/02 §4; TODO.md #4). The server sends call.sync on every (re)connect with
// the authoritative current-call state for this user; when a resume fails, the
// call.end/call.accept queued during the outage is gone, and this is what stops
// the client's ring from lying past the event.
//
// The decision (machine state × sync payload → action) is a pure function so it
// is table-testable without a browser (the 2026-07-19 machine-invariant pattern).
// The engine holds the thin glue that executes the action.
//
// Authority model:
//   - RINGING states (we are caller dialing, or callee ringing) are
//     SERVER-authoritative: the signaling server is the source of truth for
//     whether the ring still exists. A snapshot that no longer lists our call
//     ends the ring with the existing missed/connection-lost vocabulary.
//   - ESTABLISHED states (connecting / in_call / reconnecting) are
//     MEDIA-authoritative: the RTC connection is peer-to-peer and may be alive
//     even when the server has forgotten the call (server restart mid-call).
//     call.sync NEVER tears these down; peer hangup arrives via ICE/RTP loss and
//     the existing budgets handle it.

import { TIMEOUTS, type CallEndReason, type CallInvitePayload, type CallSyncPayload } from '@merkury/protocol';
import type { CallPhase } from './types.js';
import type { CallRole } from './machine.js';

/** Clock-skew allowance added to a server ring deadline (docs/02 §4). */
export const RING_SKEW_MS = 1_000;

/**
 * The remaining local ring window for an invite whose server deadline is
 * `expiresAt` (epoch ms), or undefined when no deadline is known (→ full
 * RING_MS, exactly today's behaviour). Clamped to [0, RING_MS]: a fully-elapsed
 * deadline rings ~0 ms and immediately goes missed; a fresh one rings the full
 * window.
 */
export function remainingRingMs(expiresAt: number | undefined, now: number): number | undefined {
  if (expiresAt === undefined) return undefined;
  const remaining = expiresAt - now + RING_SKEW_MS;
  return Math.max(0, Math.min(TIMEOUTS.RING_MS, remaining));
}

/** The minimal machine view the reconciler needs (keeps it decoupled). */
export interface MachineView {
  phase: CallPhase;
  callId: string | null;
  role: CallRole | null;
}

/** What the engine must do in response to a `call.sync`. */
export type ReconcileAction =
  /** Consistent, or a divergence we must not act on (established call). */
  | { t: 'none' }
  /**
   * End the current local call by driving `remoteEnd {reason}` for its callId.
   * `reason` is chosen so the machine's existing mapRemoteEnd yields the right
   * user-facing cause: `hangup` → 'missed' for a ringing callee, `timeout` →
   * 'no_answer' for a caller whose dial the server never recorded (2026-07-26
   * ruling, docs/02 §4 "Rendering call.end {timeout}": a forgotten ring most
   * often simply wasn't answered — same outcome as the local ring timeout).
   */
  | { t: 'endLocalCall'; reason: CallEndReason }
  /**
   * Feed a server-provided invite through the normal invite path (a session
   * that never received the original frame). `ringMs` bounds the ring to the
   * remaining server window; undefined ⇒ full RING_MS. Dedupe and terminal-
   * callId guards in the machine handle a replay that also arrives.
   */
  | { t: 'injectInvite'; invite: CallInvitePayload; ringMs: number | undefined }
  /**
   * Send `call.end {callId, reason:'hangup'}` — no local state change. The
   * server tracks a call this user supposedly participates in while the local
   * machine is idle: a dial we abandoned (our cancel was lost) still ringing
   * the peer, or an answered phantom poisoning both users with busy declines
   * (field 2026-07-18). Safe for sibling devices: an active call's end is
   * accepted server-side only from its pinned sessions, so a session that is
   * not in the call cannot kill one that is.
   */
  | { t: 'sendEnd'; callId: string };

/**
 * Decide how to reconcile `sync` against the local machine `view`. Pure: no I/O,
 * no clock read beyond the injected `now`.
 */
export function reconcileSync(view: MachineView, sync: CallSyncPayload, now: number): ReconcileAction {
  const server = sync.call;
  const sameCall = server !== null && view.callId !== null && server.callId === view.callId;

  switch (view.phase) {
    case 'idle':
      // Server says a ringing invite exists that we never rang: adopt it. Only
      // a callee ring carries a verbatim invite to replay.
      if (server && server.phase === 'ringing' && server.role === 'callee' && server.invite) {
        return { t: 'injectInvite', invite: server.invite, ringMs: remainingRingMs(server.expiresAt, now) };
      }
      // Server tracks a call we have no live state for — a ring we abandoned
      // as caller (still ringing the peer!) or an answered phantom: end it.
      // A ringing-callee snapshot without an invite (out-of-contract) is left
      // alone rather than risk declining a real incoming ring.
      if (server && (server.role === 'caller' || server.phase === 'answered')) {
        return { t: 'sendEnd', callId: server.callId };
      }
      return { t: 'none' };

    case 'dialing':
    case 'ringing_remote':
      // We are the caller mid-ring — server-authoritative. No matching record ⇒
      // our invite never landed or died: fail the dial rather than ring a void.
      return sameCall ? { t: 'none' } : { t: 'endLocalCall', reason: 'timeout' };

    case 'ringing':
      // Incoming ring — server-authoritative. Gone from the snapshot ⇒ the
      // caller cancelled/ended during our outage: end it as a missed call.
      return sameCall ? { t: 'none' } : { t: 'endLocalCall', reason: 'hangup' };

    default:
      // connecting / in_call / reconnecting: media-authoritative. Never tear a
      // live (or possibly-live) P2P call down from a signaling snapshot.
      return { t: 'none' };
  }
}
